// frontend/src/api/tankPhotos.ts
/**
 * API client for a tank's shots (/api/tanks/{tankId}/tank-photos) and its
 * cover (/api/tanks/{tankId}/cover). Contract:
 * docs/plans/2026-10-28-tank-shots-implementation.md, section 3.3; framing:
 * docs/plans/2026-10-29-tank-shot-framing-design.md, 6.1.
 */

import { api } from './client';
import { getLargePhoto } from './largePhoto';
import type {
  LargePhoto,
  TankCoverResponse,
  TankPhoto,
  TankPhotoFraming,
  TankPhotoIndex,
  TankPhotoListParams,
  TankPhotoPage,
  TankPhotoUpdate,
} from '@/types/tankPhotos';

function tankPath(tankId: string): string {
  return `/api/tanks/${encodeURIComponent(tankId)}`;
}

function shotsPath(tankId: string): string {
  return `${tankPath(tankId)}/tank-photos`;
}

export const tankPhotosApi = {
  /** Newest first, keyset paged: pass the previous page's `next_after` as `after`. */
  list(tankId: string, params: TankPhotoListParams = {}): Promise<TankPhotoPage> {
    const search = new URLSearchParams();
    if (params.after) search.set('after', params.after);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const query = search.toString();
    return api.get<TankPhotoPage>(`${shotsPath(tankId)}${query ? `?${query}` : ''}`);
  },

  /** Every shot as (id, date, thumb), oldest first: the compare page's presets and filmstrip. */
  index(tankId: string): Promise<TankPhotoIndex> {
    return api.get<TankPhotoIndex>(`${shotsPath(tankId)}/index`);
  },

  get(tankId: string, id: number): Promise<TankPhoto> {
    return api.get<TankPhoto>(`${shotsPath(tankId)}/${id}`);
  },

  /** The shot's large variant for the zoomed viewer; null when it has none. */
  large(tankId: string, id: number): Promise<LargePhoto | null> {
    return getLargePhoto(`${shotsPath(tankId)}/${id}/large`);
  },

  update(tankId: string, id: number, data: TankPhotoUpdate): Promise<TankPhoto> {
    return api.put<TankPhoto>(`${shotsPath(tankId)}/${id}`, data);
  },

  /** Line a shot up: save its framing against the tank's reference shot. */
  setFraming(tankId: string, id: number, framing: TankPhotoFraming): Promise<TankPhoto> {
    return api.put<TankPhoto>(`${shotsPath(tankId)}/${id}/framing`, framing);
  },

  /** Reset a shot's line up: it shows as taken again. */
  clearFraming(tankId: string, id: number): Promise<TankPhoto> {
    return api.delete<TankPhoto>(`${shotsPath(tankId)}/${id}/framing`);
  },

  async delete(tankId: string, id: number): Promise<void> {
    await api.delete(`${shotsPath(tankId)}/${id}`);
  },

  /** An id chooses that shot as the cover; null goes back to the latest shot. */
  setCover(tankId: string, tankPhotoId: number | null): Promise<TankCoverResponse> {
    return api.put<TankCoverResponse>(`${tankPath(tankId)}/cover`, {
      tank_photo_id: tankPhotoId,
    });
  },
};
